import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient.js';
import { useAuth } from './AuthContext.js';
import { geocodeAddress } from './googleMapsTravel.js';

const ProfilePage = () => {
  const { user, isApplicator, applicatorId, profile: authProfile } = useAuth();
  const [applicatorData, setApplicatorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', home_base_location: '', default_hotel_minutes: 25 });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const fetchApplicator = async () => {
    if (!applicatorId) {
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from('applicators')
      .select('*')
      .eq('id', applicatorId)
      .single();

    if (data) {
      setApplicatorData(data);
      setForm({
        name: data.name || '',
        home_base_location: data.home_base_location || '',
        default_hotel_minutes: data.default_hotel_minutes ?? 25,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchApplicator();
  }, [applicatorId]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    setSaving(true);
    setError(null);
    setSaved(false);

    let lat = null, lng = null;
    if (form.home_base_location.trim()) {
      try {
        const coords = await geocodeAddress(form.home_base_location.trim());
        lat = coords.lat;
        lng = coords.lng;
      } catch (err) {
        console.error('Geocoding failed:', err);
      }
    }

    const record = {
      name: form.name.trim(),
      home_base_location: form.home_base_location.trim() || null,
      home_base_lat: lat,
      home_base_lng: lng,
      default_hotel_minutes: parseInt(form.default_hotel_minutes) || 25,
    };

    const result = await supabase
      .from('applicators')
      .update(record)
      .eq('id', applicatorId);

    if (result.error) {
      setError(`Failed to save: ${result.error.message}`);
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      fetchApplicator();
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="text-gray-300">Loading profile...</div>;
  }

  return (
    <div className="max-w-lg">
      <h1 className="text-2xl font-bold text-white mb-6">My Profile</h1>

      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-2 rounded-md mb-4 text-sm">
          {error}
        </div>
      )}

      {isApplicator && applicatorData && (
        <form onSubmit={handleSave} className="bg-gray-800 border border-gray-700 rounded-lg p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="w-full px-3 py-2 bg-gray-700 border border-gray-700 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
              placeholder="Your name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Home Base Address</label>
            <input
              type="text"
              value={form.home_base_location}
              onChange={(e) => setForm({ ...form, home_base_location: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-700 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
              placeholder="e.g. Penticton, BC"
            />
            <p className="text-xs text-gray-400 mt-1">
              This address is used to calculate drive times at the start and end of each work day/week.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Default Hotel Travel Time (minutes)</label>
            <input
              type="number"
              min="5"
              max="60"
              value={form.default_hotel_minutes}
              onChange={(e) => setForm({ ...form, default_hotel_minutes: e.target.value })}
              className="w-24 px-3 py-2 bg-gray-700 border border-gray-700 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
            />
            <p className="text-xs text-gray-400 mt-1">
              Estimated drive time to/from hotel when travelling. Used for mid-week start/end of day.
            </p>
          </div>

          {applicatorData.home_base_lat != null && (
            <p className="text-xs text-gray-400">
              Coordinates: {Number(applicatorData.home_base_lat).toFixed(4)}, {Number(applicatorData.home_base_lng).toFixed(4)}
            </p>
          )}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-green-700 text-white text-sm font-medium rounded-md hover:bg-green-900 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
            {saved && (
              <span className="text-sm text-green-400">Saved!</span>
            )}
          </div>
        </form>
      )}

      {!isApplicator && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <p className="text-gray-300 text-sm">
            {authProfile?.display_name ? `Logged in as ${authProfile.display_name}` : 'Your account is not linked to an applicator profile.'}
          </p>
          <p className="text-gray-400 text-xs mt-2">
            Contact an admin to set up applicator access if needed.
          </p>
        </div>
      )}

      <ChangePassword />

      <div className="mt-6 text-xs text-gray-400">
        <p>Logged in as: {user?.email}</p>
      </div>
    </div>
  );
};

const ChangePassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const handleChange = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { setResult({ ok: false, msg: 'Password must be at least 6 characters' }); return; }
    if (newPassword !== confirm) { setResult({ ok: false, msg: 'Passwords do not match' }); return; }

    setSaving(true);
    setResult(null);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      setResult({ ok: false, msg: error.message });
    } else {
      setResult({ ok: true, msg: 'Password updated!' });
      setNewPassword('');
      setConfirm('');
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleChange} className="mt-6 bg-gray-800 border border-gray-700 rounded-lg p-6 space-y-4">
      <h2 className="text-lg font-semibold text-white">Change Password</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">New Password</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={6}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-700 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Confirm Password</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            className="w-full px-3 py-2 bg-gray-700 border border-gray-700 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
          />
        </div>
      </div>
      {result && (
        <p className={`text-sm ${result.ok ? 'text-green-400' : 'text-red-200'}`}>{result.msg}</p>
      )}
      <button
        type="submit"
        disabled={saving}
        className="px-4 py-2 bg-green-700 text-white text-sm font-medium rounded-md hover:bg-green-900 disabled:opacity-50 transition-colors"
      >
        {saving ? 'Updating...' : 'Update Password'}
      </button>
    </form>
  );
};

export default ProfilePage;
